import { ChevronLeft, ChevronRight } from 'assets/icons';
import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import PageForm from 'components/pageForm/PageForm';
import {
  HOURS_PER_MONTH,
  MONTHS,
  agencyPlan,
  etpPrice,
  rowPlan,
  territoryPlan,
} from 'components/stats/agencyGoals';
import { apiErrorText } from 'helper/apiErrorText';
import { euros } from 'components/stats/statsFormat';
import { CLIENT_SIZES, clientSizeLabel } from 'constants/clientSizes';
import { monthName } from 'helper/timeLabels';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import crmApi from 'server/crm/crmApi';

// What is typed stays text until it is saved: « 3 000 000 », « 1,65 ».
const toNumber = (value) =>
  Number(String(value).replace(/\s/g, '').replace(',', '.'));
const isNumber = (value) =>
  String(value).trim() !== '' &&
  Number.isFinite(toNumber(value)) &&
  toNumber(value) >= 0;
const isCount = (value) =>
  String(value).trim() === '' || /^\d+$/.test(String(value).trim());
const shown = (value) => (value ? String(value).replace('.', ',') : '');
const SIZE_KEYS = ['start', 'growth', 'max'];
// « 13,8 k€ » in the months' columns, where whole euros do not fit.
const kEuros = (value) =>
  value
    ? `${(value / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} k€`
    : '–';
const shortMonth = (month) => {
  const name = new Date(2026, month, 1).toLocaleDateString('fr-FR', {
    month: 'short',
  });
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const formOf = (goals) => ({
  global: shown(goals.global),
  coefficient: shown(goals.coefficient),
  sizes: Object.fromEntries(
    CLIENT_SIZES.map((size) => [
      size,
      Object.fromEntries(
        SIZE_KEYS.map((key) => [key, shown(goals.sizes[size][key])]),
      ),
    ]),
  ),
  plan: Object.fromEntries(
    goals.territories.map((territory) => [
      territory,
      Object.fromEntries(
        CLIENT_SIZES.map((size) => [
          size,
          Array.from({ length: MONTHS }, (_, month) =>
            shown(goals.plan[territory]?.[size]?.[month]),
          ),
        ]),
      ),
    ]),
  ),
});

// The form as numbers, for the arithmetic and the save.
const goalsOfForm = (form, year) => ({
  year,
  global: toNumber(form.global || 0),
  coefficient: toNumber(form.coefficient || 0),
  sizes: Object.fromEntries(
    CLIENT_SIZES.map((size) => [
      size,
      Object.fromEntries(
        SIZE_KEYS.map((key) => [key, toNumber(form.sizes[size][key] || 0)]),
      ),
    ]),
  ),
  plan: Object.fromEntries(
    Object.entries(form.plan).map(([territory, row]) => [
      territory,
      Object.fromEntries(
        CLIENT_SIZES.map((size) => [
          size,
          row[size].map((count) => Number(count || 0)),
        ]),
      ),
    ]),
  ),
});

const Field = ({
  label,
  value,
  onChange,
  suffix,
  width = 'w-20',
  valid = true,
}) => (
  <span className="flex items-center gap-1.5">
    <input
      type="text"
      inputMode="decimal"
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`h-9 rounded-md border bg-white px-2 text-right text-sm text-gray-900 focus:outline-none ${width} ${valid ? 'border-gray-200 focus:border-yellow-500' : 'border-red-500'}`}
    />
    {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
  </span>
);

/**
 * « Paramètres · Objectifs de l'agence », as the « Plan d'action » of the
 * « Tableau pilotage »: the revenue of the year, then the new clients to sign
 * each month in each territory and size. An ETP is worth the SMIC of a month
 * × the average billing coefficient; a client grows each month from the one
 * it signs. The plan uses up the overall goal: what is left shows above it.
 */
const AdminAgencyGoals = () => {
  const { text } = useText('agencyGoals');
  const { language } = UseContext();
  const { call, ready } = useCrmContext();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [smic, setSmic] = useState(null);
  // The refusal of the last read, null when it went through.
  const [failed, setFailed] = useState(null);
  const [saving, setSaving] = useState(false);

  // The SMIC an hour, gross, as Jobbiz knows it (the missions' wage uses it).
  useEffect(() => {
    ApiDatabase.getMinimumWage(
      (data) => setSmic(Number(data?.hourly?.gross) || 0),
      () => setSmic(0),
    );
  }, []);

  const load = useCallback(() => {
    setFailed(null);
    setForm(null);
    call(crmApi.getAgencyGoals, { year }).then(
      (goals) => {
        setSaved(goals);
        setForm(formOf(goals));
      },
      (error) => setFailed(error ?? {}),
    );
  }, [call, year]);
  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  const set = (path, value) =>
    setForm((current) => {
      const next = JSON.parse(JSON.stringify(current));
      let target = next;
      path.slice(0, -1).forEach((key) => (target = target[key]));
      target[path[path.length - 1]] = value;
      return next;
    });

  const content = () => {
    if (failed)
      return (
        <CompactEmptyState
          title={text('loadError')}
          description={
            apiErrorText(failed, { language }) || undefined
          }
          action={{ label: text('retry'), onClick: load }}
        />
      );
    if (!form || smic === null)
      return (
        <div
          className="h-64 animate-pulse rounded-lg bg-gray-100"
          aria-busy="true"
        />
      );
    const territories = saved.territories;
    const goals = goalsOfForm(form, year);
    const price = etpPrice(smic, goals.coefficient);
    const total = agencyPlan(goals, territories, price);
    const left = Math.round(goals.global - total.revenue);
    const valid =
      isNumber(form.global || 0) &&
      isNumber(form.coefficient) &&
      CLIENT_SIZES.every((size) =>
        SIZE_KEYS.every((key) => isNumber(form.sizes[size][key])),
      ) &&
      territories.every((territory) =>
        CLIENT_SIZES.every((size) => form.plan[territory][size].every(isCount)),
      );
    const changed = JSON.stringify(form) !== JSON.stringify(formOf(saved));
    let tone = 'bg-blue-50 text-blue-700';
    let status = text('left', { amount: euros(left) });
    if (left === 0 && goals.global > 0) {
      tone = 'bg-green-50 text-green-700';
      status = text('shared');
    }
    if (left < 0) {
      tone = 'bg-red-50 text-red-700';
      status = text('over', { amount: euros(-left) });
    }

    const save = () => {
      setSaving(true);
      call(crmApi.saveAgencyGoals, goals).then(
        (result) => {
          setSaving(false);
          const next = { ...result, territories };
          setSaved(next);
          setForm(formOf(next));
          toast.success(text('saved'));
        },
        (error) => {
          setSaving(false);
          toast.error(
            apiErrorText(error, { language }),
          );
        },
      );
    };

    const monthCells = (months) =>
      months.map((value, month) => (
        <td
          key={month}
          className="whitespace-nowrap px-1 py-2.5 text-right text-xs font-semibold text-gray-900"
        >
          {kEuros(value)}
        </td>
      ));
    const sizeName = (size) => clientSizeLabel(language, size);

    return (
      <>
        <section className="flex flex-col gap-4 rounded-lg border border-gray-100 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {text('globalTitle', { year })}
              </h2>
              <p className="text-sm text-gray-500">{text('globalText')}</p>
            </div>
            <Field
              label={text('globalTitle', { year })}
              value={form.global}
              onChange={(value) => set(['global'], value)}
              suffix="€"
              width="w-36"
              valid={isNumber(form.global || 0)}
            />
          </div>
          <p
            className={`rounded-lg px-4 py-3 text-sm font-medium ${tone}`}
            role="status"
          >
            {status}
          </p>
        </section>

        <section className="flex flex-col gap-4 rounded-lg border border-gray-100 bg-white p-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {text('etpTitle')}
            </h2>
            <p className="text-sm text-gray-500">{text('etpText')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
            <span>
              {text('smic', {
                amount: smic
                  ? smic.toLocaleString('fr-FR', { minimumFractionDigits: 2 })
                  : '–',
              })}
            </span>
            <span className="text-gray-400">×</span>
            <span>
              {text('hours', {
                hours: HOURS_PER_MONTH.toLocaleString('fr-FR'),
              })}
            </span>
            <span className="text-gray-400">×</span>
            <span className="flex items-center gap-2">
              {text('coefficient')}
              <Field
                label={text('coefficient')}
                value={form.coefficient}
                onChange={(value) => set(['coefficient'], value)}
                valid={isNumber(form.coefficient)}
              />
            </span>
            <span className="text-gray-400">=</span>
            <span className="font-semibold text-gray-900">
              {text('price', { amount: euros(Math.round(price)) })}
            </span>
          </div>
          <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
            <p className="text-sm text-gray-500">{text('rampText')}</p>
            {CLIENT_SIZES.map((size) => (
              <div
                key={size}
                className="flex flex-wrap items-center gap-2 text-sm text-gray-700"
              >
                <span className="w-32 font-medium text-gray-900">
                  {sizeName(size)}
                </span>
                {text('rampStart')}
                <Field
                  label={text('rampStartOf', { size: sizeName(size) })}
                  value={form.sizes[size].start}
                  onChange={(value) => set(['sizes', size, 'start'], value)}
                  suffix="ETP"
                  width="w-16"
                  valid={isNumber(form.sizes[size].start)}
                />
                {text('rampGrowth')}
                <Field
                  label={text('rampGrowthOf', { size: sizeName(size) })}
                  value={form.sizes[size].growth}
                  onChange={(value) => set(['sizes', size, 'growth'], value)}
                  suffix={text('perMonth')}
                  width="w-16"
                  valid={isNumber(form.sizes[size].growth)}
                />
                {text('rampMax')}
                <Field
                  label={text('rampMaxOf', { size: sizeName(size) })}
                  value={form.sizes[size].max}
                  onChange={(value) => set(['sizes', size, 'max'], value)}
                  suffix="ETP"
                  width="w-16"
                  valid={isNumber(form.sizes[size].max)}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-gray-100 bg-white">
          <div className="px-4 py-3">
            <h2 className="text-base font-semibold text-gray-900">
              {text('planTitle')}
            </h2>
            <p className="text-sm text-gray-500">{text('planText')}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm">
              <thead>
                <tr className="border-y border-gray-100 bg-gray-50 text-xs font-medium uppercase tracking-[0.2px] text-gray-500">
                  <th className="sticky left-0 bg-gray-50 py-3 pl-4 pr-2 text-left font-medium">
                    {text('colTerritory')}
                  </th>
                  {Array.from({ length: MONTHS }, (_, month) => (
                    <th
                      key={month}
                      className="px-1 py-3 text-right font-medium"
                    >
                      {shortMonth(month)}
                    </th>
                  ))}
                  <th className="px-2 py-3 text-right font-medium">
                    {text('colClients')}
                  </th>
                  <th className="py-3 pl-2 pr-4 text-right font-medium">
                    {text('colRevenue')}
                  </th>
                </tr>
              </thead>
              {territories.map((territory) => {
                const sub = territoryPlan(goals, territory, price);
                return (
                  <tbody key={territory} className="border-b border-gray-100">
                    <tr className="bg-[#fcfcfc]">
                      <td className="sticky left-0 bg-[#fcfcfc] py-2.5 pl-4 pr-2 font-semibold text-gray-900">
                        {territory}
                      </td>
                      {monthCells(sub.months)}
                      <td className="px-2 py-2.5 text-right font-semibold text-gray-900">
                        {sub.clients}
                      </td>
                      <td className="py-2.5 whitespace-nowrap pl-2 pr-4 text-right font-semibold text-gray-900">
                        {euros(Math.round(sub.revenue))}
                      </td>
                    </tr>
                    {CLIENT_SIZES.map((size) => {
                      const row = rowPlan(goals, territory, size, price);
                      return (
                        <tr key={size}>
                          <td className="sticky left-0 whitespace-nowrap bg-white py-1.5 pl-8 pr-2 text-gray-700">
                            {sizeName(size)}
                          </td>
                          {form.plan[territory][size].map((count, month) => (
                            <td key={month} className="px-1 py-1.5">
                              <input
                                type="text"
                                inputMode="numeric"
                                aria-label={text('clientsIn', {
                                  size: sizeName(size),
                                  territory,
                                  month: monthName(month),
                                })}
                                value={count}
                                onChange={(event) =>
                                  set(
                                    ['plan', territory, size, month],
                                    event.target.value,
                                  )
                                }
                                className={`h-8 w-full min-w-[40px] rounded-md border bg-white px-1.5 text-right text-sm text-gray-900 focus:outline-none ${isCount(count) ? 'border-gray-200 focus:border-yellow-500' : 'border-red-500'}`}
                              />
                            </td>
                          ))}
                          <td className="px-2 py-1.5 text-right text-gray-700">
                            {row.clients}
                          </td>
                          <td className="py-1.5 whitespace-nowrap pl-2 pr-4 text-right text-gray-700">
                            {euros(Math.round(row.revenue))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                );
              })}
              <tfoot>
                <tr className="bg-gray-50">
                  <td className="sticky left-0 bg-gray-50 py-3 pl-4 pr-2 font-semibold text-gray-900">
                    {text('total')}
                  </td>
                  {monthCells(total.months)}
                  <td className="px-2 py-3 text-right font-semibold text-gray-900">
                    {total.clients}
                  </td>
                  <td className="py-3 whitespace-nowrap pl-2 pr-4 text-right font-semibold text-gray-900">
                    {euros(Math.round(total.revenue))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        {changed && (
          <div className="sticky bottom-0 flex items-center justify-end gap-3 rounded-lg border border-gray-100 bg-white px-4 py-3">
            <span className="mr-auto text-xs text-gray-500">
              {valid ? text('unsaved') : text('invalid')}
            </span>
            <Button
              color="SECONDARY"
              size="SM"
              onClick={() => setForm(formOf(saved))}
            >
              {text('cancel')}
            </Button>
            <Button
              color="PRIMARY"
              size="SM"
              disabled={!valid || saving}
              onClick={save}
            >
              {text('save')}
            </Button>
          </div>
        )}
      </>
    );
  };

  const arrow =
    'flex size-9 items-center justify-center rounded-md border border-gray-200 bg-white transition-colors hover:bg-gray-50';
  return (
    <PageForm title={text('pageTitle')}>
      <div className="flex w-full flex-col gap-5 px-4 py-6 lg:px-8">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={arrow}
            aria-label={text('previousYear')}
            onClick={() => setYear(year - 1)}
          >
            <ChevronLeft wh={18} color="#374151" />
          </button>
          <span className="min-w-[56px] text-center text-base font-semibold text-gray-900">
            {year}
          </span>
          <button
            type="button"
            className={arrow}
            aria-label={text('nextYear')}
            onClick={() => setYear(year + 1)}
          >
            <ChevronRight wh={18} color="#374151" />
          </button>
        </div>
        {content()}
      </div>
    </PageForm>
  );
};

export default AdminAgencyGoals;
